import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  Loader2, Download, AlertTriangle, BookOpen, Search, ThumbsUp, ThumbsDown, ShieldAlert,
  Check, ArrowLeft, ArrowRight, Lock, Info, TrendingDown, FileDown, Save, ShieldCheck,
} from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { Toggle } from '@/components/ui/Toggle'
import { RichTextEditor } from '@/components/ui/RichTextEditor'
import { RichHtml } from '@/components/ui/RichHtml'
import { richToPlain } from '@/lib/richText'
import { toast } from '@/components/ui/Toast'
import { extractApiError } from '@/lib/api-errors'
import { useAuthority } from '@/hooks/useAuthority'
import { brl, dateTimeBR, maskPhoneInput, maskCep, maskCnpj, maskCpf, onlyDigits } from '@/lib/format'
import { normalizeForMatch } from '@/lib/validators'
import { LookupService, type State, type City as LookupCity } from '@/services/modules/LookupService'
import { SalesLinkService } from '@/services/modules/SalesLinkService'
import {
  ProposalsService, ProposalStagesService,
  type ProposalStage, type ProposalQuote, type CommercialProposal, type OfferLine,
} from '@/services/modules/ProposalsService'
import { ProposalSetupService, type ProposalOption, type ProposalTextBlock } from '@/services/modules/ProposalSetupService'
import { ProposalTemplatesService } from '@/services/modules/ProposalTemplatesService'
import { AutomationReportsService, type AutomationReport } from '@/services/modules/AutomationReportsService'

/**
 * Nova proposta / edição — assistente no fluxo da planilha "Simulador":
 * linha → simulação → empresa → condições → revisão.
 *
 * Esta tela também vai ser usada por representantes: nada de preço cheio,
 * desconto, peso de cobertura ou versão de tabela. Só o que se mostra ao
 * cliente (valor por vida e total mensal).
 */

/** A tabela é de 5 em 5 vidas — o campo respeita isso. */
const LIVES_STEP = 5

type Step = 1 | 2 | 3 | 4 | 5
const STEPS: { n: Step; label: string }[] = [
  { n: 1, label: 'Linha' },
  { n: 2, label: 'Simulação' },
  { n: 3, label: 'Empresa' },
  { n: 4, label: 'Condições' },
  { n: 5, label: 'Revisão' },
]

const todayISO = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const snakeToCamel = (s: string) => s.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())

/** Em qual passo mora cada campo (para levar o usuário ao erro). */
function stepForField(field: string): Step {
  if (field === 'proposalCategoryId') return 1
  if (['livesCount', 'tierIds', 'itemIds', 'pricing'].includes(field)) return 2
  if (['validityStart', 'contractTypeOptionId', 'minMonthsOptionId', 'paymentDayOptionId', 'firstPaymentOptionId',
    'paymentMethodIds', 'proposalDate', 'signingCityOptionId', 'stageId', 'observations', 'additionalText'].includes(field)
    || field.startsWith('textBlock')) return 4
  return 3
}


export function ProposalFormPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id?: string }>()
  const [params, setParams] = useSearchParams()
  const isEdit = !!id

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const initialStep = Number(params.get('etapa')) as Step
  const [step, setStep] = useState<Step>(isEdit && initialStep >= 1 && initialStep <= 5 ? initialStep : 1)
  const [visited, setVisited] = useState<Step>(isEdit ? 5 : 1)

  // Catálogos
  const [lines, setLines] = useState<OfferLine[]>([])
  const [options, setOptions] = useState<ProposalOption[]>([])
  const [textBlocks, setTextBlocks] = useState<ProposalTextBlock[]>([])
  const [stages, setStages] = useState<ProposalStage[]>([])

  // Empresa
  const [companyName, setCompanyName] = useState('')
  const [document, setDocument] = useState('')
  const [recipientName, setRecipientName] = useState('')
  const [recipientEmail, setRecipientEmail] = useState('')
  const [recipientPhone, setRecipientPhone] = useState('')
  const [address, setAddress] = useState('')
  const [number, setNumber] = useState('')
  const [complement, setComplement] = useState('')
  const [district, setDistrict] = useState('')
  const [postalCode, setPostalCode] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [legalRepName, setLegalRepName] = useState('')
  const [legalRepDocument, setLegalRepDocument] = useState('')
  const [states, setStates] = useState<State[]>([])
  const [cities, setCities] = useState<LookupCity[]>([])
  const [stateId, setStateId] = useState('')
  const [cityId, setCityId] = useState('')
  const [cepLoading, setCepLoading] = useState(false)
  const [cnpjLoading, setCnpjLoading] = useState(false)

  // Simulação
  const [categoryId, setCategoryId] = useState('')
  const [livesCount, setLivesCount] = useState<number>(LIVES_STEP)
  const [tierIds, setTierIds] = useState<string[]>([])
  const [itemIds, setItemIds] = useState<string[]>([])
  const [quote, setQuote] = useState<ProposalQuote | null>(null)
  const [quoting, setQuoting] = useState(false)
  const [quoteError, setQuoteError] = useState<string | null>(null)

  // Condições
  const [proposalDate, setProposalDate] = useState('')
  const [signingCityId, setSigningCityId] = useState('')
  const [stageId, setStageId] = useState('')
  const [contractTypeOptionId, setContractTypeOptionId] = useState('')
  const [minMonthsOptionId, setMinMonthsOptionId] = useState('')
  const [paymentDayOptionId, setPaymentDayOptionId] = useState('')
  const [firstPaymentOptionId, setFirstPaymentOptionId] = useState('')
  const [paymentMethodIds, setPaymentMethodIds] = useState<string[]>([])
  const [validityStart, setValidityStart] = useState('')
  const [textBlockIds, setTextBlockIds] = useState<string[]>([])
  const [additionalText, setAdditionalText] = useState('')
  const [observations, setObservations] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Permissões e aprovação do texto adicional
  const { can } = useAuthority()
  const canApprove = can('commercial.proposals.approve')
  const canSave = can(isEdit ? 'commercial.proposals.edit' : 'commercial.proposals.create')
  const canGenerate = can('commercial.proposals.generate')
  const canGenerateManual = can('commercial.proposals.generate_manual')
  const [approval, setApproval] = useState<CommercialProposal['approval']>(undefined)
  const [rejectOpen, setRejectOpen] = useState(false)
  const [rejectNote, setRejectNote] = useState('')
  const [deciding, setDeciding] = useState(false)

  // Geração assíncrona (PDF e manual)
  const [report, setReport] = useState<AutomationReport | null>(null)
  const [generating, setGenerating] = useState<'pdf' | 'manual' | null>(null)
  const timer = useRef<number | undefined>(undefined)
  useEffect(() => () => { if (timer.current) window.clearInterval(timer.current) }, [])

  useEffect(() => {
    let alive = true
    Promise.all([
      ProposalsService.catalog(id).catch(() => [] as OfferLine[]),
      ProposalSetupService.options().catch(() => [] as ProposalOption[]),
      ProposalSetupService.textBlocks().catch(() => [] as ProposalTextBlock[]),
      isEdit && id ? ProposalsService.show(id) : Promise.resolve(null),
      isEdit ? Promise.resolve(null) : ProposalTemplatesService.get().catch(() => null),
      ProposalStagesService.list({ size: 100, status: 'ATIVO' }).then(r => r.data).catch(() => [] as ProposalStage[]),
      LookupService.states().catch(() => [] as State[]),
    ]).then(([lns, opts, texts, p, tpl, stgs, sts]) => {
      if (!alive) return
      setLines(lns as OfferLine[])
      setOptions((opts as ProposalOption[]).filter(o => o.status === 'ATIVO'))
      setTextBlocks((texts as ProposalTextBlock[]).filter(t => t.status === 'ATIVO'))
      setStates(sts as State[])
      setStages([...(stgs as ProposalStage[])].sort((a, b) => a.order - b.order))

      if (p) {
        setCompanyName(p.companyName); setDocument(p.document ? maskCnpj(p.document) : '')
        setRecipientName(p.recipientName ?? ''); setRecipientEmail(p.recipientEmail ?? '')
        setRecipientPhone(p.recipientPhone ? maskPhoneInput(p.recipientPhone) : '')
        setAddress(p.address ?? ''); setNumber(p.number ?? '')
        setComplement(p.complement ?? ''); setDistrict(p.district ?? '')
        setPostalCode(p.postalCode ? maskCep(p.postalCode) : '')
        setSigningCityId(p.signingCityOptionId ?? '')
        setCity(p.city ?? ''); setState(p.state ?? '')
        setLegalRepName(p.legalRepName ?? '')
        setLegalRepDocument(p.legalRepDocument ? maskCpf(p.legalRepDocument) : '')
        const st = (sts as State[]).find(s => s.abbreviation === p.state)
        if (st) {
          setStateId(st.id)
          LookupService.cities(st.id).then(cs => {
            if (!alive) return
            setCities(cs)
            const ci = cs.find(c => normalizeForMatch(c.city) === normalizeForMatch(p.city ?? ''))
            if (ci) setCityId(ci.id)
          }).catch(() => { /* mantém o texto salvo */ })
        }
        setProposalDate(p.proposalDate ?? ''); setStageId(p.stageId ?? '')
        setCategoryId(p.proposalCategoryId ?? '')
        setLivesCount(p.livesCount || LIVES_STEP)
        setTierIds(p.tierIds ?? [])
        setItemIds(p.itemIds ?? [])
        setContractTypeOptionId(p.contractTypeOptionId ?? '')
        setMinMonthsOptionId(p.minMonthsOptionId ?? '')
        setPaymentDayOptionId(p.paymentDayOptionId ?? '')
        setFirstPaymentOptionId(p.firstPaymentOptionId ?? '')
        setPaymentMethodIds(p.paymentMethodIds ?? [])
        setValidityStart(p.validityStart ?? '')
        setTextBlockIds(p.textBlockIds ?? [])
        setAdditionalText(p.additionalText ?? ''); setObservations(p.observations ?? '')
        setApproval(p.approval)
      } else {
        // Nova: data de hoje, padrões das condições e observações do modelo.
        setProposalDate(todayISO())
        setObservations(tpl?.defaultObservations ?? '')
        const active = (opts as ProposalOption[]).filter(o => o.status === 'ATIVO')
        const def = (kind: string) => active.find(o => o.kind === kind && o.isDefault)?.id ?? ''
        setContractTypeOptionId(def('CONTRACT_TYPE'))
        setMinMonthsOptionId(def('MIN_MONTHS'))
        setPaymentDayOptionId(def('PAYMENT_DAY'))
        setFirstPaymentOptionId(def('FIRST_PAYMENT'))
        setPaymentMethodIds(active.filter(o => o.kind === 'PAYMENT_METHOD' && o.isDefault).map(o => o.id))
        setSigningCityId(def('SIGNING_CITY'))
      }
    }).finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [isEdit, id])

  const line = useMemo(() => lines.find(l => l.id === categoryId) ?? null, [lines, categoryId])
  const tierWord = line?.tierLabel === 'Níveis' ? 'nível' : 'modalidade'

  /** Escolher a linha marca tudo dela: o comercial tira o que não for oferecer. */
  function chooseLine(l: OfferLine) {
    if (l.id === categoryId) return
    setCategoryId(l.id)
    setTierIds(l.tiers.map(t => t.id))
    setItemIds(l.items.map(i => i.id))
    setErrors(e => ({ ...e, proposalCategoryId: '' }))
  }

  // Blocos de texto da linha escolhida (ou de todas).
  const availableBlocks = useMemo(
    () => textBlocks.filter(b => !b.categoryId || b.categoryId === categoryId),
    [textBlocks, categoryId],
  )
  const optionsOf = useCallback(
    (kind: string) => options.filter(o => o.kind === kind).map(o => ({ value: o.id, label: o.label })),
    [options],
  )
  const optionLabel = (oid: string) => options.find(o => o.id === oid)?.label ?? ''

  // --------------------------------------------------------- Simulação viva
  const runQuote = useCallback(async () => {
    if (!categoryId || !livesCount || tierIds.length === 0 || itemIds.length === 0) {
      setQuote(null); setQuoteError(null)
      return
    }
    setQuoting(true)
    try {
      setQuote(await ProposalsService.simulate({
        proposalId: id, proposalCategoryId: categoryId, livesCount, tierIds, itemIds,
      }))
      setQuoteError(null)
    } catch (e) {
      setQuote(null)
      setQuoteError(extractApiError(e).message)
    } finally { setQuoting(false) }
  }, [id, categoryId, livesCount, tierIds, itemIds])

  useEffect(() => {
    const t = window.setTimeout(() => { void runQuote() }, 350)
    return () => window.clearTimeout(t)
  }, [runQuote])

  // ---------------------------------------------------------- Empresa
  function handleStateChange(nextId: string) {
    setStateId(nextId); setCityId(''); setCity(''); setCities([])
    setState(states.find(s => s.id === nextId)?.abbreviation ?? '')
    if (nextId) LookupService.cities(nextId).then(setCities).catch(() => setCities([]))
  }
  function handleCityChange(nextId: string) {
    setCityId(nextId)
    setCity(cities.find(c => c.id === nextId)?.city ?? '')
  }

  async function resolveCity(stateAbbr: string, cityName: string) {
    const st = states.find(s => s.abbreviation === stateAbbr)
    if (!st) return false
    setStateId(st.id); setState(st.abbreviation)
    const cs = await LookupService.cities(st.id)
    setCities(cs)
    const ci = cs.find(c => normalizeForMatch(c.city) === normalizeForMatch(cityName))
    if (ci) { setCityId(ci.id); setCity(ci.city) } else { setCityId(''); setCity(cityName) }
    return true
  }

  async function lookupCep() {
    const cleaned = onlyDigits(postalCode)
    if (cleaned.length !== 8) { toast.error({ title: 'CEP inválido', description: 'O CEP precisa ter 8 dígitos.' }); return }
    setCepLoading(true)
    try {
      const data = await SalesLinkService.lookupCep(cleaned)
      if (!data) { toast.error({ title: 'CEP não encontrado', description: 'Confira o CEP digitado.' }); return }
      setAddress(data.address)
      if (data.district) setDistrict(data.district)
      if (!await resolveCity(data.stateAbbr, data.cityName)) {
        toast.warning({ title: 'Estado não encontrado', description: 'Escolha o estado e a cidade manualmente.' })
      }
    } catch {
      toast.error({ title: 'Não foi possível buscar o CEP', description: 'Confira a conexão e tente de novo.' })
    } finally { setCepLoading(false) }
  }

  async function lookupCnpj() {
    const cleaned = onlyDigits(document)
    if (cleaned.length !== 14) { toast.error({ title: 'CNPJ inválido', description: 'O CNPJ precisa ter 14 dígitos.' }); return }
    setCnpjLoading(true)
    try {
      const data = await LookupService.lookupCnpj(cleaned)
      if (!data) { toast.error({ title: 'CNPJ não encontrado', description: 'Confira o CNPJ digitado.' }); return }
      if (data.razaoSocial) setCompanyName(data.razaoSocial)
      if (data.postalCode) setPostalCode(maskCep(data.postalCode))
      if (data.email && !recipientEmail) setRecipientEmail(data.email)
      if (data.phone && !recipientPhone) setRecipientPhone(maskPhoneInput(data.phone))
      setAddress(data.address ?? '')
      if (data.number) setNumber(String(data.number))
      if (data.district) setDistrict(data.district)
      if (data.stateAbbr) await resolveCity(data.stateAbbr, data.cityName)
      toast.success({ title: 'Empresa encontrada', description: 'Os dados foram preenchidos. Confira antes de seguir.' })
    } catch {
      toast.error({ title: 'Não foi possível buscar o CNPJ', description: 'Confira a conexão e tente de novo.' })
    } finally { setCnpjLoading(false) }
  }

  function toggleIn(list: string[], setList: (v: string[]) => void, value: string) {
    setList(list.includes(value) ? list.filter(x => x !== value) : [...list, value])
  }

  // ---------------------------------------------------------- Validação
  function errorsOf(s: Step): Record<string, string> {
    const e: Record<string, string> = {}
    if (s === 1 && !categoryId) e.proposalCategoryId = 'Escolha a linha da proposta.'
    if (s === 2) {
      if (!livesCount || livesCount < 1) e.livesCount = 'Informe quantas vidas a empresa tem.'
      if (tierIds.length === 0) e.tierIds = `Escolha pelo menos uma ${tierWord}.`
      if (itemIds.length === 0) e.itemIds = 'Deixe pelo menos uma cobertura.'
    }
    if (s === 3) {
      if (onlyDigits(document).length !== 14) e.document = 'Informe o CNPJ completo.'
      if (!companyName.trim()) e.companyName = 'Informe a razão social.'
      if (!recipientName.trim()) e.recipientName = 'Informe quem recebe a proposta.'
      if (onlyDigits(postalCode).length !== 8) e.postalCode = 'Informe o CEP.'
      if (!address.trim()) e.address = 'Informe o endereço.'
      if (!number.trim()) e.number = 'Informe o número.'
      if (!district.trim()) e.district = 'Informe o bairro.'
      if (!state.trim()) e.state = 'Escolha o estado.'
      if (!city.trim()) e.city = 'Escolha a cidade.'
    }
    return e
  }
  const stepOk = (s: Step) => Object.keys(errorsOf(s)).length === 0

  function goTo(target: Step) {
    if (target > step) {
      for (let s = step; s < target; s = (s + 1) as Step) {
        const e = errorsOf(s)
        if (Object.keys(e).length > 0) {
          setErrors(e); setStep(s)
          toast.error({ title: 'Falta preencher', description: Object.values(e)[0] })
          return
        }
      }
    }
    setErrors({})
    setStep(target)
    setVisited(v => (target > v ? target : v))
    if (isEdit) setParams({ etapa: String(target) }, { replace: true })
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // ---------------------------------------------------------- Salvar
  async function submit() {
    if (!canSave) return
    for (const s of [1, 2, 3] as Step[]) {
      const e = errorsOf(s)
      if (Object.keys(e).length > 0) { setErrors(e); setStep(s); toast.error({ title: 'Falta preencher', description: Object.values(e)[0] }); return }
    }
    setSaving(true)
    try {
      const payload = {
        companyName: companyName.trim(),
        document: onlyDigits(document) || null,
        recipientName: recipientName.trim() || null,
        recipientEmail: recipientEmail.trim() || null,
        recipientPhone: onlyDigits(recipientPhone) || null,
        address: address.trim() || null,
        number: number.trim() || null,
        complement: complement.trim() || null,
        district: district.trim() || null,
        postalCode: onlyDigits(postalCode) || null,
        signingCityOptionId: signingCityId || null,
        city: city.trim() || null,
        state: state.trim().toUpperCase() || null,
        legalRepName: legalRepName.trim() || null,
        legalRepDocument: onlyDigits(legalRepDocument) || null,
        proposalDate: proposalDate || null,
        stageId: stageId || null,
        proposalCategoryId: categoryId,
        livesCount,
        tierIds,
        itemIds,
        validityStart: validityStart || null,
        contractTypeOptionId: contractTypeOptionId || null,
        minMonthsOptionId: minMonthsOptionId || null,
        paymentDayOptionId: paymentDayOptionId || null,
        firstPaymentOptionId: firstPaymentOptionId || null,
        paymentMethodIds,
        textBlockIds,
        additionalText: additionalText || null,
        observations: observations || null,
      }
      if (isEdit && id) {
        const p = await ProposalsService.update(id, payload)
        setApproval(p.approval)
        toast.success('Proposta salva.')
      } else {
        const p = await ProposalsService.create(payload)
        toast.success({ title: 'Proposta criada', description: 'Agora é só gerar o PDF.' })
        navigate(`/commercial/proposals/${p.id}/editar?etapa=5`, { replace: true })
      }
    } catch (e) {
      const parsed = extractApiError(e)
      const mapped: Record<string, string> = {}
      for (const [field, message] of Object.entries(parsed.fields ?? {})) mapped[snakeToCamel(field)] = message
      if (Object.keys(mapped).length > 0) { setErrors(mapped); setStep(stepForField(Object.keys(mapped)[0])) }
      toast.error({ title: 'Não foi possível salvar', description: parsed.message })
    } finally { setSaving(false) }
  }

  async function decide(action: 'approve' | 'reject') {
    if (!id) return
    if (action === 'reject' && !rejectNote.trim()) {
      toast.error({ title: 'Informe o motivo', description: 'Quem escreveu precisa saber o que ajustar.' })
      return
    }
    setDeciding(true)
    try {
      const updated = action === 'approve' ? await ProposalsService.approve(id) : await ProposalsService.reject(id, rejectNote.trim())
      setApproval(updated.approval)
      setRejectOpen(false); setRejectNote('')
      toast.success(action === 'approve' ? 'Texto aprovado.' : 'Texto reprovado.')
    } catch (e) {
      toast.error({ title: 'Não foi possível concluir', description: extractApiError(e).message })
    } finally { setDeciding(false) }
  }

  function pollReport(rid: string, what: 'pdf' | 'manual') {
    if (timer.current) window.clearInterval(timer.current)
    timer.current = window.setInterval(async () => {
      try {
        const fresh = await AutomationReportsService.show(rid)
        if (fresh.status === 'CONCLUIDO' || fresh.status === 'FALHOU') {
          window.clearInterval(timer.current); timer.current = undefined
          setGenerating(null); setReport(fresh)
          const label = what === 'pdf' ? 'A proposta' : 'O manual'
          if (fresh.status === 'CONCLUIDO') toast.success(`${label} está pronto.`)
          else toast.error(fresh.errorMessage ?? `Não foi possível gerar ${what === 'pdf' ? 'a proposta' : 'o manual'}.`)
        }
      } catch { /* tenta de novo */ }
    }, 4000) as unknown as number
  }

  async function generate(what: 'pdf' | 'manual') {
    if (!id) return
    setGenerating(what); setReport(null)
    try {
      const r = what === 'pdf' ? await ProposalsService.generate(id) : await ProposalsService.generateManual(id)
      pollReport(r.id, what)
      toast.success('Gerando… o arquivo fica pronto em alguns segundos.')
    } catch (e) {
      setGenerating(null)
      toast.error({ title: 'Não foi possível gerar', description: extractApiError(e).message })
    }
  }

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 size={28} className="animate-spin text-gray-400" /></div>
  }

  const done = report?.status === 'CONCLUIDO' && report.filePath
  const selectedItems = line?.items.filter(i => itemIds.includes(i.id)) ?? []

  // --------------------------------------------------------------- Passos
  const step1 = (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Qual linha você vai oferecer?</h2>
        <p className="mt-1 text-sm text-gray-600">Cada linha tem as próprias modalidades e coberturas. Na próxima etapa você escolhe o que entra na proposta e vê os valores.</p>
      </div>
      {lines.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-600">
          Nenhuma linha disponível para proposta no momento. Fale com o administrador do sistema.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {lines.map(l => {
            const on = l.id === categoryId
            const resto = l.items.length - 5
            return (
              <button key={l.id} type="button" onClick={() => chooseLine(l)} aria-pressed={on}
                className={`flex cursor-pointer flex-col gap-4 rounded-xl bg-white p-5 text-left transition ${on ? 'ring-2 ring-brand-500 shadow-[0_0_0_4px_rgba(27,132,255,0.15)]' : 'ring-1 ring-gray-200 hover:ring-gray-300'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-lg font-bold text-gray-900">{l.name}</p>
                    <RichHtml html={l.description} className="mt-0.5 text-sm text-gray-600" />
                  </div>
                  <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${on ? 'bg-brand-500' : 'ring-2 ring-gray-300'}`}>
                    {on && <Check size={14} className="text-white" strokeWidth={3} />}
                  </span>
                </div>
                <div>
                  <p className={`mb-1.5 text-[11px] font-semibold uppercase tracking-wide ${on ? 'text-brand-700' : 'text-gray-500'}`}>{l.tierLabel}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {l.tiers.map(t => (
                      <span key={t.id} className={`rounded-full px-2.5 py-1 text-xs ${on ? 'bg-brand-50 text-brand-700' : 'bg-gray-100 text-gray-700'}`}>{t.name}</span>
                    ))}
                  </div>
                </div>
                <ul className="space-y-1.5 text-sm text-gray-700">
                  {l.items.slice(0, 5).map(i => (
                    <li key={i.id} className="flex gap-2"><Check size={15} className="mt-0.5 shrink-0 text-emerald-600" />{i.name}</li>
                  ))}
                  {resto > 0 && <li className="pl-6 text-xs text-gray-500">e mais {resto} cobertura{resto > 1 ? 's' : ''}</li>}
                </ul>
              </button>
            )
          })}
        </div>
      )}
      {errors.proposalCategoryId && <p className="text-sm text-rose-600">{errors.proposalCategoryId}</p>}
    </div>
  )

  const step2 = line && (
    <div className="flex flex-col gap-5 lg:flex-row lg:items-start">
      <div className="min-w-0 flex-1 space-y-4">
        <Card title="Quantas vidas a empresa tem?" subtitle="O valor é calculado de 5 em 5 vidas. Quanto mais vidas, menor o valor por vida.">
          <div className="flex flex-wrap items-center gap-4">
            <div className="inline-flex items-center overflow-hidden rounded-lg ring-1 ring-gray-300">
              <button type="button" aria-label="Menos 5 vidas" onClick={() => setLivesCount(v => Math.max(1, (v || 1) - LIVES_STEP))}
                className="h-11 w-11 cursor-pointer bg-gray-50 text-lg text-gray-700 hover:bg-gray-100">−</button>
              <input type="number" min={1} step={LIVES_STEP} aria-label="Número de vidas" value={livesCount || ''}
                onChange={e => setLivesCount(Math.max(0, parseInt(e.target.value, 10) || 0))}
                className="h-11 w-24 border-0 text-center text-lg font-bold text-gray-900 focus:outline-none" />
              <button type="button" aria-label="Mais 5 vidas" onClick={() => setLivesCount(v => (v || 0) + LIVES_STEP)}
                className="h-11 w-11 cursor-pointer bg-gray-50 text-lg text-gray-700 hover:bg-gray-100">+</button>
            </div>
            {quote && (
              <span className="text-sm text-gray-600">
                {quote.livesAdjusted ? <>{quote.livesRequested} vidas são calculadas como <strong>{quote.livesUsed}</strong></> : <>Valores para <strong>{quote.livesUsed}</strong> vidas</>}
              </span>
            )}
          </div>
          {quote?.nextBandFrom && (
            <p className="mt-3 flex items-center gap-2 rounded-lg bg-brand-50 px-3 py-2 text-sm text-brand-700">
              <TrendingDown size={16} /> A partir de {quote.nextBandFrom} vidas, o valor por vida fica menor.
            </p>
          )}
          {errors.livesCount && <p className="mt-2 text-sm text-rose-600">{errors.livesCount}</p>}
        </Card>

        <Card title={`${line.tierLabel} na proposta`} subtitle={line.tierLabel === 'Níveis'
          ? 'Escolha os níveis que a empresa vai poder oferecer. Cada um aparece com o seu valor no PDF.'
          : 'Escolha o que a empresa vai poder oferecer aos colaboradores. Cada modalidade aparece com o seu valor no PDF.'}>
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            {line.tiers.map(t => {
              const on = tierIds.includes(t.id)
              return (
                <button key={t.id} type="button" aria-pressed={on} onClick={() => toggleIn(tierIds, setTierIds, t.id)}
                  className={`flex cursor-pointer flex-col gap-1 rounded-xl p-3.5 text-left transition ${on ? 'bg-brand-50 ring-2 ring-brand-500' : 'bg-white ring-1 ring-gray-200 hover:ring-gray-300'}`}>
                  <span className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-gray-900">{t.name}</span>
                    <span className={`flex h-5 w-5 items-center justify-center rounded-md ${on ? 'bg-brand-500' : 'ring-2 ring-gray-300'}`}>{on && <Check size={12} className="text-white" strokeWidth={3} />}</span>
                  </span>
                  <span className="text-xs text-gray-600">{t.beneficiaries || '—'}</span>
                </button>
              )
            })}
          </div>
          {errors.tierIds && <p className="mt-2 text-sm text-rose-600">{errors.tierIds}</p>}
        </Card>

        <Card title="Coberturas e benefícios" subtitle="Deixe ligado o que entra na proposta. Os valores se ajustam na hora."
          aside={<span className="text-xs text-gray-500">{itemIds.length} de {line.items.length} incluídas</span>}>
          <div className="space-y-2">
            {line.items.map(i => {
              const on = itemIds.includes(i.id)
              const porNivel = i.tierTexts.filter(tt => tierIds.includes(tt.tierId))
              return (
                <div key={i.id} className={`flex flex-wrap items-start gap-3 rounded-xl px-3.5 py-3 ${on ? 'bg-white ring-1 ring-gray-200' : 'bg-gray-50 ring-1 ring-dashed ring-gray-300'}`}>
                  <Toggle checked={on} onChange={() => toggleIn(itemIds, setItemIds, i.id)} />
                  <div className="min-w-0 flex-1 basis-60">
                    <p className="flex flex-wrap items-center gap-1.5 text-sm font-semibold text-gray-900">
                      {i.name}
                      {i.insurance && <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-semibold text-brand-700"><ShieldCheck size={12} />Securitária</span>}
                    </p>
                    <RichHtml html={i.text} className="mt-0.5 text-xs text-gray-600" />
                    {porNivel.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {porNivel.map(tt => (
                          <span key={tt.tierId} className="rounded-md bg-gray-100 px-2 py-0.5 text-[11px] text-gray-700">
                            <strong>{line.tiers.find(t => t.id === tt.tierId)?.name}:</strong> {richToPlain(tt.text)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  {i.grace && <span className="whitespace-nowrap rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-700">Carência: {i.grace}</span>}
                </div>
              )
            })}
          </div>
          {errors.itemIds && <p className="mt-2 text-sm text-rose-600">{errors.itemIds}</p>}
        </Card>
      </div>

      <aside className="w-full shrink-0 lg:sticky lg:top-4 lg:w-[360px]">
        <QuotePanel quote={quote} quoting={quoting} error={quoteError} noTiers={tierIds.length === 0} tierWord={tierWord} />
      </aside>
    </div>
  )

  const step3 = (
    <Card title="Dados da empresa" subtitle="Comece pelo CNPJ: a lupa preenche razão social e endereço.">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Input label="CNPJ" required value={document} onChange={e => setDocument(maskCnpj(e.target.value))}
          placeholder="00.000.000/0001-00" error={errors.document}
          actionRight={<SearchButton loading={cnpjLoading} onClick={lookupCnpj} label="Buscar dados pelo CNPJ" />} />
        <Input label="CEP" required value={postalCode} onChange={e => setPostalCode(maskCep(e.target.value))}
          placeholder="00000-000" error={errors.postalCode}
          actionRight={<SearchButton loading={cepLoading} onClick={lookupCep} label="Buscar endereço pelo CEP" />} />
        <Input label="Razão social" required wrapperClassName="md:col-span-2" value={companyName} onChange={e => setCompanyName(e.target.value)} error={errors.companyName} />
        <Input label="Endereço" required wrapperClassName="md:col-span-2" value={address} onChange={e => setAddress(e.target.value)} placeholder="Rua, avenida" error={errors.address} />
        <Input label="Número" required value={number} onChange={e => setNumber(e.target.value)} error={errors.number} />
        <Input label="Complemento" value={complement} onChange={e => setComplement(e.target.value)} placeholder="Sala, bloco" error={errors.complement} />
        <Input label="Bairro" required value={district} onChange={e => setDistrict(e.target.value)} error={errors.district} />
        <Select label="Estado" required value={stateId} onChange={e => handleStateChange(e.target.value)}
          options={states.map(s => ({ value: s.id, label: `${s.state} (${s.abbreviation})` }))}
          placeholder="Escolha o estado" searchPlaceholder="Buscar estado" error={errors.state} />
        <Select label="Cidade" required value={cityId} onChange={e => handleCityChange(e.target.value)}
          options={cities.map(c => ({ value: c.id, label: c.city }))}
          placeholder={stateId ? 'Escolha a cidade' : 'Escolha o estado antes'} searchPlaceholder="Buscar cidade"
          error={errors.city} hint={!cityId && city ? `Salvo: ${city}` : undefined} />
      </div>
      <div className="mt-6 border-t border-gray-100 pt-5">
        <p className="mb-3 text-sm font-semibold text-gray-800">Quem recebe a proposta</p>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Input label="Aos cuidados de" required value={recipientName} onChange={e => setRecipientName(e.target.value)} placeholder="Nome do responsável" error={errors.recipientName} />
          <Input label="E-mail" type="email" value={recipientEmail} onChange={e => setRecipientEmail(e.target.value)} placeholder="nome@empresa.com.br" error={errors.recipientEmail} />
          <Input label="Telefone" value={recipientPhone} onChange={e => setRecipientPhone(maskPhoneInput(e.target.value))} placeholder="(11) 99999-9999" error={errors.recipientPhone} />
        </div>
      </div>
      <div className="mt-6 border-t border-gray-100 pt-5">
        <p className="mb-3 text-sm font-semibold text-gray-800">Quem assina pela empresa <span className="font-normal text-gray-500">(opcional)</span></p>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Input label="Representante legal" value={legalRepName} onChange={e => setLegalRepName(e.target.value)} error={errors.legalRepName} />
          <Input label="CPF do representante" value={legalRepDocument} onChange={e => setLegalRepDocument(maskCpf(e.target.value))} placeholder="000.000.000-00" error={errors.legalRepDocument} />
        </div>
      </div>
    </Card>
  )

  const step4 = (
    <div className="space-y-4">
      <Card title="Contratação e pagamento" subtitle="As opções vêm do cadastro — escolha a que vale para esta empresa.">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Select label="Tipo de contratação" value={contractTypeOptionId} onChange={e => setContractTypeOptionId(e.target.value)}
            options={optionsOf('CONTRACT_TYPE')} placeholder="Escolha" error={errors.contractTypeOptionId} />
          <Select label="Contrato mínimo" value={minMonthsOptionId} onChange={e => setMinMonthsOptionId(e.target.value)}
            options={optionsOf('MIN_MONTHS')} placeholder="Escolha" error={errors.minMonthsOptionId} />
          <Input label="Início da vigência" type="date" value={validityStart} onChange={e => setValidityStart(e.target.value)}
            clearable={false} error={errors.validityStart} hint="Pode ficar em branco e ser combinado depois." />
          <Select label="Vencimento mensal" value={paymentDayOptionId} onChange={e => setPaymentDayOptionId(e.target.value)}
            options={optionsOf('PAYMENT_DAY')} placeholder="Escolha" error={errors.paymentDayOptionId} />
          <Select label="Primeiro pagamento" value={firstPaymentOptionId} onChange={e => setFirstPaymentOptionId(e.target.value)}
            options={optionsOf('FIRST_PAYMENT')} placeholder="Escolha" error={errors.firstPaymentOptionId} />
        </div>
        <div className="mt-5">
          <p className="mb-2 text-sm font-medium text-gray-700">Formas de pagamento</p>
          <div className="flex flex-wrap gap-2">
            {options.filter(o => o.kind === 'PAYMENT_METHOD').map(o => {
              const on = paymentMethodIds.includes(o.id)
              return (
                <button key={o.id} type="button" aria-pressed={on} onClick={() => toggleIn(paymentMethodIds, setPaymentMethodIds, o.id)}
                  className={`min-h-9 cursor-pointer rounded-full px-3.5 py-1.5 text-sm ring-1 transition ${on ? 'bg-brand-50 text-brand-700 ring-brand-300' : 'bg-white text-gray-700 ring-gray-200 hover:bg-gray-50'}`}>
                  {o.label}
                </button>
              )
            })}
          </div>
        </div>
      </Card>

      <Card title="Documento" subtitle="Data e cidade que abrem a assinatura do PDF.">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Input label="Data da proposta" type="date" value={proposalDate} onChange={e => setProposalDate(e.target.value)} clearable={false} error={errors.proposalDate} />
          <Select label="Cidade da assinatura" value={signingCityId} onChange={e => setSigningCityId(e.target.value)}
            options={optionsOf('SIGNING_CITY')} placeholder="Escolha" error={errors.signingCityOptionId}
            hint="Ex.: “Jundiaí, 14 de outubro de 2026”. Não é a cidade do cliente." />
          <Select label="Etapa do funil" value={stageId} onChange={e => setStageId(e.target.value)}
            options={stages.map(s => ({ value: s.id, label: s.stage }))} placeholder="Primeira etapa" searchPlaceholder="Buscar etapa" />
        </div>
      </Card>

      <Card title="Textos do PDF" subtitle="Seções prontas que entram no documento, na ordem abaixo. Algumas já vêm marcadas por padrão.">
        {availableBlocks.length === 0 ? (
          <p className="text-sm text-gray-500">Nenhum texto disponível para esta linha.</p>
        ) : (
          <div className="space-y-2">
            {availableBlocks.map(b => (
              <div key={b.id} className="flex items-start gap-3 rounded-lg bg-gray-50/70 px-3 py-2.5 ring-1 ring-gray-200">
                <Toggle checked={textBlockIds.includes(b.id)} onChange={() => toggleIn(textBlockIds, setTextBlockIds, b.id)} />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800">{b.title || 'Sem título'}</p>
                  <RichHtml html={b.content} className="text-xs text-gray-500" />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title="Observações e condições comerciais" subtitle="Saem no PDF do jeito que estão aqui. Mudar aqui vale só para esta proposta.">
        <RichTextEditor value={observations} onChange={setObservations} />
      </Card>

      <Card title="Texto adicional" subtitle="Texto livre, impresso depois das condições. Opcional.">
        <p className="mb-3 flex gap-2 rounded-lg bg-amber-50 px-3 py-2.5 text-sm text-amber-900 ring-1 ring-amber-200">
          <Info size={16} className="mt-0.5 shrink-0" />
          Escrever aqui manda a proposta para aprovação: o PDF só sai depois que alguém com permissão liberar o texto.
        </p>
        <RichTextEditor value={additionalText} onChange={setAdditionalText} />
      </Card>
    </div>
  )

  const avisos: { ok: boolean; text: string; go?: Step }[] = [
    { ok: stepOk(3), text: stepOk(3) ? 'Dados da empresa completos.' : 'Faltam dados da empresa.', go: 3 },
    { ok: tierIds.length > 0 && itemIds.length > 0, text: `${tierIds.length} ${line?.tierLabel === 'Níveis' ? 'nível(is)' : 'modalidade(s)'} e ${itemIds.length} cobertura(s) na proposta.`, go: 2 },
    ...(validityStart ? [] : [{ ok: false, text: 'Falta o início da vigência. Sem ele, o PDF sai sem a data de início.', go: 4 as Step }]),
    ...(approval?.status === 'PENDENTE' ? [{ ok: false, text: 'O texto adicional precisa ser aprovado antes de gerar o PDF.' }] : []),
  ]

  const step5 = (
    <div className="flex flex-col gap-5 lg:flex-row lg:items-start">
      <div className="min-w-0 flex-1 space-y-4">
        <Summary title="Empresa" onEdit={() => goTo(3)}>
          <Dl items={[
            ['Razão social', companyName || '—'],
            ['CNPJ', document || '—'],
            ['Aos cuidados de', recipientName || '—'],
            ['Contato', [recipientEmail, recipientPhone].filter(Boolean).join(' · ') || '—'],
          ]} />
        </Summary>

        <Summary title={`Linha ${line?.name ?? ''} · ${quote?.livesUsed ?? livesCount} vidas`} onEdit={() => goTo(2)}>
          {quote ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[460px] text-sm">
                <thead><tr className="text-left text-[11px] uppercase tracking-wide text-gray-500">
                  <th className="py-2 font-semibold">{line?.tierLabel === 'Níveis' ? 'Nível' : 'Modalidade'}</th>
                  <th className="py-2 font-semibold">Quem pode aderir</th>
                  <th className="py-2 text-right font-semibold">Por vida</th>
                  <th className="py-2 text-right font-semibold">Total mensal</th>
                </tr></thead>
                <tbody>
                  {quote.tiers.map(t => (
                    <tr key={t.id} className="border-t border-gray-100">
                      <td className="py-2.5 font-semibold text-gray-900">{t.name}</td>
                      <td className="py-2.5 text-gray-600">{t.beneficiaries || '—'}</td>
                      <td className="py-2.5 text-right font-semibold text-gray-900">{brl(t.total)}</td>
                      <td className="py-2.5 text-right text-gray-700">{brl(t.monthlyTotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="text-sm text-gray-500">{quoteError ?? 'Calculando…'}</p>}
          <div className="mt-3 flex flex-wrap gap-1.5">
            {selectedItems.map(i => (
              <span key={i.id} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs ${i.insurance ? 'bg-brand-50 text-brand-700' : 'bg-emerald-50 text-emerald-800'}`}>
                {i.insurance && <ShieldCheck size={12} aria-label="Securitária" />}{i.name}
              </span>
            ))}
          </div>
        </Summary>

        <Summary title="Condições" onEdit={() => goTo(4)}>
          <Dl items={[
            ['Contratação', optionLabel(contractTypeOptionId) || '—'],
            ['Contrato mínimo', optionLabel(minMonthsOptionId) || '—'],
            ['Pagamento', [paymentMethodIds.map(optionLabel).join(', '), optionLabel(paymentDayOptionId)].filter(Boolean).join(' · ') || '—'],
            ['Primeiro pagamento', optionLabel(firstPaymentOptionId) || '—'],
            ['Início da vigência', validityStart ? new Date(`${validityStart}T12:00:00`).toLocaleDateString('pt-BR') : 'a definir'],
            ['Cidade da assinatura', optionLabel(signingCityId) || '—'],
          ]} />
        </Summary>

        <Card title="Antes de gerar">
          <ul className="space-y-2">
            {avisos.map((a, k) => (
              <li key={k} className={`flex items-center gap-2.5 rounded-lg text-sm ${a.ok ? 'text-gray-700' : 'bg-amber-50 px-3 py-2 text-amber-900'}`}>
                {a.ok ? <Check size={17} className="shrink-0 text-emerald-600" strokeWidth={2.5} /> : <AlertTriangle size={17} className="shrink-0 text-amber-600" />}
                <span className="flex-1">{a.text}</span>
                {!a.ok && a.go && <button type="button" onClick={() => goTo(a.go!)} className="cursor-pointer text-sm font-semibold text-brand-700 hover:underline">Resolver</button>}
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <aside className="w-full shrink-0 space-y-3 lg:sticky lg:top-4 lg:w-[340px]">
        <div className="rounded-xl bg-white p-4 ring-1 ring-gray-200">
          <p className="text-sm font-semibold text-gray-900">{isEdit ? 'Documento' : 'Pronto para salvar'}</p>
          <p className="mt-1 text-xs text-gray-600">
            {isEdit
              ? 'Salve as alterações e gere o PDF. Cada geração usa o que está salvo.'
              : 'Salve a proposta para poder gerar o PDF.'}
          </p>
          <div className="mt-3 space-y-2">
            <Button className="w-full" onClick={submit} loading={saving} disabled={!canSave} iconLeft={<Save size={15} />}
              title={canSave ? undefined : `Sem permissão para ${isEdit ? 'editar' : 'criar'} propostas`}>
              {isEdit ? 'Salvar alterações' : 'Salvar proposta'}
            </Button>
            {isEdit && canGenerate && (
              <Button className="w-full" variant="secondary" onClick={() => generate('pdf')} disabled={!!generating || approval?.blocked}
                iconLeft={generating === 'pdf' ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />}>
                {generating === 'pdf' ? 'Gerando PDF…' : 'Gerar PDF'}
              </Button>
            )}
            {isEdit && canGenerateManual && (
              <Button className="w-full" variant="ghost" onClick={() => generate('manual')} disabled={!!generating}
                iconLeft={generating === 'manual' ? <Loader2 size={15} className="animate-spin" /> : <BookOpen size={15} />}>
                {generating === 'manual' ? 'Gerando manual…' : 'Gerar manual de utilização'}
              </Button>
            )}
            {done && (
              <a href={report!.filePath!} target="_blank" rel="noopener noreferrer"
                className="flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-emerald-50 text-sm font-semibold text-emerald-700 hover:bg-emerald-100">
                <Download size={15} /> Baixar arquivo
              </a>
            )}
            {report?.status === 'FALHOU' && (
              <p className="flex items-center gap-1.5 text-xs text-rose-600"><AlertTriangle size={14} /> {report.errorMessage ?? 'A geração falhou.'}</p>
            )}
          </div>
        </div>
        <p className="flex gap-2 px-1 text-xs text-gray-500">
          <Lock size={14} className="mt-0.5 shrink-0" /> Os valores ficam garantidos nesta proposta.
        </p>
      </aside>
    </div>
  )

  const content = { 1: step1, 2: step2, 3: step3, 4: step4, 5: step5 }[step]

  return (
    <>
      <PageHeader
        title={isEdit ? `Proposta · ${companyName || 'sem nome'}` : 'Nova proposta'}
        subtitle={line ? `Linha ${line.name}` : 'Escolha a linha, simule e gere o PDF'}
        crumbs={[{ label: 'Comercial' }, { label: 'Propostas', to: '/commercial/proposals' }, { label: isEdit ? 'Editar' : 'Nova' }]}
        backTo="/commercial/proposals"
        actions={isEdit && step !== 5 ? (
          <Button variant="secondary" onClick={submit} loading={saving} disabled={!canSave} iconLeft={<Save size={14} />}>Salvar</Button>
        ) : undefined}
      />

      {approval && approval.status !== 'NAO_REQUERIDA' && (
        <ApprovalBanner approval={approval} canApprove={canApprove} deciding={deciding}
          rejectOpen={rejectOpen} rejectNote={rejectNote} setRejectOpen={setRejectOpen} setRejectNote={setRejectNote} decide={decide} />
      )}

      <nav aria-label="Etapas da proposta" className="mb-5">
        <ol className="grid grid-cols-5 gap-2">
          {STEPS.map(s => {
            const active = s.n === step
            const doneStep = s.n < step || (s.n <= visited && s.n !== step && stepOk(s.n))
            const reachable = s.n <= visited || s.n <= step + 1
            return (
              <li key={s.n}>
                <button type="button" disabled={!reachable} onClick={() => goTo(s.n)} aria-current={active ? 'step' : undefined}
                  className={`flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left transition disabled:cursor-not-allowed ${active ? 'bg-white ring-2 ring-brand-500' : 'cursor-pointer bg-white ring-1 ring-gray-200 hover:ring-gray-300'}`}>
                  <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${active ? 'bg-brand-500 text-white' : doneStep ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
                    {doneStep && !active ? <Check size={13} strokeWidth={3} /> : s.n}
                  </span>
                  <span className={`hidden break-words text-sm sm:inline ${active ? 'font-semibold text-brand-700' : 'text-gray-700'}`}>
                    {s.n === 1 && line && !active ? `Linha: ${line.name}` : s.label}
                  </span>
                </button>
              </li>
            )
          })}
        </ol>
      </nav>

      {content}

      {step < 5 && (
        <div className="mt-6 flex items-center justify-between gap-3 border-t border-gray-200 pt-4">
          {step > 1
            ? <Button variant="secondary" onClick={() => goTo((step - 1) as Step)} iconLeft={<ArrowLeft size={15} />}>Voltar</Button>
            : <span />}
          <Button onClick={() => goTo((step + 1) as Step)} iconRight={<ArrowRight size={15} />}>
            {step === 1 ? 'Montar a proposta' : 'Continuar'}
          </Button>
        </div>
      )}
    </>
  )
}

// ------------------------------------------------------------------ peças

function Card({ title, subtitle, aside, children }: { title: string; subtitle?: string; aside?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="rounded-xl bg-white p-5 ring-1 ring-gray-200">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          {subtitle && <p className="mt-0.5 text-sm text-gray-600">{subtitle}</p>}
        </div>
        {aside}
      </div>
      {children}
    </section>
  )
}

function Summary({ title, onEdit, children }: { title: string; onEdit: () => void; children: React.ReactNode }) {
  return (
    <section className="rounded-xl bg-white p-5 ring-1 ring-gray-200">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        <button type="button" onClick={onEdit} className="cursor-pointer text-sm font-semibold text-brand-700 hover:underline">Alterar</button>
      </div>
      {children}
    </section>
  )
}

function Dl({ items }: { items: [string, string][] }) {
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
      {items.map(([k, v]) => (
        <div key={k}><dt className="text-xs text-gray-500">{k}</dt><dd className="text-gray-900">{v}</dd></div>
      ))}
    </dl>
  )
}

function SearchButton({ loading, onClick, label }: { loading: boolean; onClick: () => void; label: string }) {
  return (
    <button type="button" onClick={onClick} disabled={loading} aria-label={label} title={label}
      className="inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-gray-500 transition hover:bg-gray-100 hover:text-brand-600 disabled:cursor-not-allowed disabled:opacity-50">
      {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
    </button>
  )
}

function QuotePanel({ quote, quoting, error, noTiers, tierWord }: {
  quote: ProposalQuote | null; quoting: boolean; error: string | null; noTiers: boolean; tierWord: string
}) {
  return (
    <div className="overflow-hidden rounded-xl bg-white ring-1 ring-gray-200">
      <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-4 py-3.5">
        <h2 className="text-sm font-semibold text-gray-900">Valores da proposta</h2>
        {quoting ? <Loader2 size={14} className="animate-spin text-gray-400" /> : quote && <span className="text-xs text-gray-500">para {quote.livesUsed} vidas</span>}
      </div>
      {noTiers && <p className="px-4 py-4 text-sm text-amber-800">Escolha pelo menos uma {tierWord} para ver os valores.</p>}
      {!noTiers && error && <p className="px-4 py-4 text-sm text-rose-600">{error}</p>}
      {!noTiers && !error && !quote && <p className="px-4 py-4 text-sm text-gray-500">Calculando…</p>}
      {quote && !noTiers && !error && quote.tiers.map(t => (
        <div key={t.id} className="border-b border-gray-100 px-4 py-3">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-sm font-semibold text-gray-900">{t.name}</span>
            <span className="text-lg font-bold text-gray-900">{brl(t.total)}<span className="text-xs font-medium text-gray-500"> por vida</span></span>
          </div>
          <div className="mt-0.5 flex justify-between text-xs text-gray-600"><span>Total mensal</span><span>{brl(t.monthlyTotal)}</span></div>
        </div>
      ))}
      <div className="space-y-1.5 bg-gray-50 px-4 py-3 text-xs text-gray-600">
        <p className="flex gap-2"><Lock size={13} className="mt-0.5 shrink-0 text-emerald-600" />Os valores ficam garantidos nesta proposta.</p>
        {quote && <p className="flex gap-2"><Info size={13} className="mt-0.5 shrink-0" />Cada {tierWord} é calculada para as {quote.livesUsed} vidas informadas.</p>}
      </div>
    </div>
  )
}

function ApprovalBanner({ approval, canApprove, deciding, rejectOpen, rejectNote, setRejectOpen, setRejectNote, decide }: {
  approval: NonNullable<CommercialProposal['approval']>
  canApprove: boolean
  deciding: boolean
  rejectOpen: boolean
  rejectNote: string
  setRejectOpen: (v: boolean) => void
  setRejectNote: (v: string) => void
  decide: (a: 'approve' | 'reject') => void
}) {
  const tone = approval.status === 'APROVADA' ? 'emerald' : approval.status === 'REPROVADA' ? 'rose' : 'amber'
  const box = { emerald: 'bg-emerald-50 ring-emerald-200', rose: 'bg-rose-50 ring-rose-200', amber: 'bg-amber-50 ring-amber-200' }[tone]
  const ink = { emerald: 'text-emerald-800', rose: 'text-rose-800', amber: 'text-amber-800' }[tone]
  return (
    <div className={`mb-4 rounded-xl p-4 ring-1 ${box}`}>
      <div className="flex flex-wrap items-start gap-3">
        <ShieldAlert size={18} className={ink} />
        <div className="min-w-0 flex-1">
          <p className={`text-sm font-semibold ${ink}`}>
            {approval.status === 'APROVADA' && 'Texto adicional aprovado'}
            {approval.status === 'REPROVADA' && 'Texto adicional reprovado'}
            {approval.status === 'PENDENTE' && 'Texto adicional aguardando aprovação'}
          </p>
          <p className="mt-0.5 text-sm text-gray-700">
            {approval.status === 'PENDENTE' && 'O PDF só sai depois que alguém com permissão liberar o texto escrito nesta proposta.'}
            {approval.status === 'REPROVADA' && 'Ajuste o texto adicional: ao salvar, ele volta para aprovação.'}
            {approval.status === 'APROVADA' && `Liberado${approval.by ? ` por ${approval.by}` : ''}${approval.at ? ` em ${dateTimeBR(approval.at)}` : ''}.`}
          </p>
          {approval.note && <p className="mt-1.5 rounded bg-white/70 px-2 py-1 text-sm text-gray-700 ring-1 ring-black/5"><strong>Observação:</strong> {approval.note}</p>}
        </div>
        {canApprove && approval.status !== 'APROVADA' && (
          <div className="flex shrink-0 items-center gap-2">
            <Button size="sm" variant="secondary" loading={deciding} iconLeft={<ThumbsDown size={13} />} onClick={() => setRejectOpen(true)}>Reprovar</Button>
            <Button size="sm" loading={deciding} iconLeft={<ThumbsUp size={13} />} onClick={() => decide('approve')}>Aprovar</Button>
          </div>
        )}
      </div>
      {rejectOpen && (
        <div className="mt-3 space-y-2 border-t border-black/5 pt-3">
          <Textarea label="Motivo da reprovação" rows={3} value={rejectNote} onChange={e => setRejectNote(e.target.value)}
            placeholder="O que precisa ser ajustado no texto" hint="Quem escreveu vê este motivo ao abrir a proposta." />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => { setRejectOpen(false); setRejectNote('') }}>Cancelar</Button>
            <Button size="sm" loading={deciding} onClick={() => decide('reject')}>Confirmar reprovação</Button>
          </div>
        </div>
      )}
    </div>
  )
}
